import React from "react"
import cx from "classnames"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { CountBadge } from "../count-badge"
import { Icon } from "../icon"
import { Tooltip } from "../tooltip"
import type { IconName } from "../icon/assets/icon-map"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabProps = {
  /**
   * Optional count rendered in a `CountBadge` after `children`.
   */
  count?: number
  /**
   * Optional icon rendered before `children`.
   */
  icon?: IconName
  /**
   * Optional tooltip, shown on hover and keyboard focus and announced as the
   * tab's description. Use it to explain why a tab is `disabled`: disabled tabs
   * stay focusable, so keyboard and screen reader users reach the reason too.
   */
  tooltip?: React.ReactNode
} & TabsPrimitive.Tab.Props

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabStyles = [
  "outline-2 outline-transparent",
  "border-b border-transparent data-[active]:border-gray-900",
  "py-4",
  "inline-flex items-center gap-x-2",
  "text-sm leading-6 font-semibold text-gray-700 data-[active]:text-gray-900",
  "transition-colors duration-200",
  "not-aria-disabled:hover:border-gray-700 data-[active]:hover:border-gray-900 focus-visible:outline-gray-900",
  "aria-disabled:text-gray-600 aria-disabled:cursor-not-allowed"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab.
 *
 * Renders Base UI's `Tabs.Tab` as a `<button role="tab">`. Active state is
 * derived from the ancestor `PageSecondaryHeader.TabsRoot`'s `value` matching
 * this tab's `value`.
 *
 * Must be a descendant of `PageSecondaryHeader.Tabs`.
 */
export const PageSecondaryHeaderTab = React.forwardRef<
  HTMLButtonElement,
  PageSecondaryHeaderTabProps
>(function PageSecondaryHeaderTab(
  { children, className, count, icon, tooltip, value, ...props },
  ref
) {
  const tab = (
    <TabsPrimitive.Tab
      ref={ref}
      value={value}
      className={cx(pageSecondaryHeaderTabStyles, className)}
      {...props}
    >
      {/* Keeps the icon out of the label's sibling list, so toggling it never
          inserts before text a page translator has replaced. */}
      <span className="contents">{icon && <Icon name={icon} size="xs" />}</span>
      {children}
      {count !== undefined && <CountBadge count={count} />}
    </TabsPrimitive.Tab>
  )

  return (
    <Tooltip disabled={!tooltip}>
      <Tooltip.Trigger render={tab} />
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  )
})
